import { Component, OnInit, OnDestroy, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { HeaderComponent } from '../header/header.component';
import { NavbarComponent } from '../navbar/navbar.component';
import { LoadingSpinnerComponent } from '../shared/loading-spinner.component';
import { ReceiverStatusTabsComponent } from './receiver-status-tabs.component';
import { ReceiverPassportCellComponent } from './receiver-passport-cell.component';
import { SearchService } from '../shared/search.service';
import { FilterService } from '../shared/filter.service';
import { PassportService, PassportRecord } from '../shared/passport.service';
import { PassportDetailsStore } from '../shared/passport-details.store';
import { UiStateService } from '../shared/ui-state.service';
import { OfflineStorageService } from '../shared/offline-storage.service';
import { CommentsNotificationService } from '../shared/comments-notification.service';
import { PassportHelperService } from '../shared/passport-helper.service';
import { PersonaRoutingService } from '../shared/persona-routing.service';
import { PassportCacheService } from '../shared/passport-cache.service';
import { SyncService } from '../shared/sync.service';
import { BaseStatusPersonaComponent } from '../shared/base-status-persona.component';
import { PassportMainWithDraftDto } from '../interfaces/api-responses';
import { environment } from '../../environments/environment';
import { DateWithOrdinalPipe } from '../shared/date-with-ordinal.pipe';
import { NoPassportsComponent } from '../shared/no-passports.component';
import { HistoryPanelService } from '../shared/history-panel.service';
import { CommentsPanelService } from '../shared/comments-panel.service';
import { ActionButtonsComponent } from '../shared/desktop-table-templates.component';
import { DesktopEmptyStateComponent } from '../shared/desktop-empty-state.component';
import { PERSONA_NAVBAR_ITEMS } from '../shared/constants/navbar.constant';
import {
  clearReceiverListRestoreState,
  parseReceiverListSnapshot,
  peekExpectRestore,
  saveReceiverListSnapshot,
  type ReceiverListSnapshot
} from './receiver-list-state.storage';

// Auto-scroll constants for desktop drag operations
const DESKTOP_AUTO_SCROLL_THRESHOLD = 100; // pixels from edge where scrolling activates
const DESKTOP_HEADER_HEIGHT = 252; // Fixed header/filter area height
const DESKTOP_MAX_SCROLL_SPEED = 15; // pixels per frame

/** Desktop "Show rows" values — must stay in sync with receiver.component.html rows dropdown */
const RECEIVER_DESKTOP_SHOW_ROWS_OPTIONS = new Set([10, 25, 50, 100, 150, 200, 250]);

function normalizeReceiverDesktopShowRows(size: number): number {
  if (RECEIVER_DESKTOP_SHOW_ROWS_OPTIONS.has(size)) {
    return size;
  }
  return 10;
}

/** Single pass over passports for tab counts + visible list (debounced search via `filterQuery`). */
interface ReceiverListDerived {
  readonly visibleRecords: PassportRecord[];
  readonly shuntingCount: number;
  readonly receivingCount: number;
  readonly completedCount: number;
}

/**
 * Receiver persona component
 * Extends BaseStatusPersonaComponent with custom status mapping and drag-and-drop
 */
@Component({
  selector: 'app-receiver',
  standalone: true,
  imports: [HeaderComponent, NavbarComponent, ReceiverStatusTabsComponent, ReceiverPassportCellComponent, CommonModule, LoadingSpinnerComponent, DateWithOrdinalPipe, NoPassportsComponent, FormsModule, ActionButtonsComponent, DesktopEmptyStateComponent],
  templateUrl: './receiver.component.html',
  styleUrl: './receiver.component.css'
})
export class ReceiverComponent extends BaseStatusPersonaComponent implements OnInit, OnDestroy {
  /** Navbar items constant for template */
  readonly navbarItems = PERSONA_NAVBAR_ITEMS;
  
  receiverFilters: Array<{ id: string; label: string; options: string[] }> = [];
  
  // Drag and drop state
  draggedRecordId: string | null = null;
  longPressTimer: any = null;
  longPressActive = false;

  // Desktop Drag State
  desktopDragActiveRecordId: string | null = null;
  desktopHoveredRecordId: string | null = null; // Track hovered row for drag handle visibility
  private desktopLongPressTimer: any;
  private ignoreNextClick = false; // Flag to prevent navigation after long press

  // Desktop Auto-scroll State
  private desktopAutoScrollAnimationId: number | null = null;
  private desktopAutoScrollDirection: 'up' | 'down' | null = null;
  private desktopAutoScrollSpeed = 0;

  /** When set, applied after passports load (returning from passport details). */
  private pendingReceiverRestore: ReceiverListSnapshot | null = null;
  private passportIdToScrollAfterRestore: string | null = null;

  /**
   * One reactive derivation: search uses `filterQuery` (debounced) so typing stays responsive;
   * tab badges and the list share the same pass over `passports.records()`.
   */
  readonly receiverListDerived = computed((): ReceiverListDerived => {
    const all = this.passports.records();
    const filters = this.filterService.filters();
    const queryNorm = (this.searchService.filterQuery() || '').trim().toLowerCase();
    const tab = this.ui.statusSelected();

    let shuntingCount = 0;
    let receivingCount = 0;
    let completedCount = 0;
    const visibleRecords: PassportRecord[] = [];

    for (const r of all) {
      if (!this.recordMatchesSearchAndFilters(r, queryNorm, filters)) {
        continue;
      }
      if (r.status === 'pending') {
        shuntingCount += 1;
      } else if (r.status === 'outgoing') {
        receivingCount += 1;
      } else if (r.status === 'completed') {
        completedCount += 1;
      }
      if (r.status === tab) {
        visibleRecords.push(r);
      }
    }

    return { visibleRecords, shuntingCount, receivingCount, completedCount };
  });

  protected get personaName(): string {
    return 'receiver';
  }

  get headerFilters(): Array<{ id: string; label: string; options: string[] }> {
    return this.receiverFilters;
  }

  get isShuntingTab(): boolean {
    return this.ui.statusSelected() === 'pending';
  }

  constructor(
    http: HttpClient,
    passports: PassportService,
    searchService: SearchService,
    filterService: FilterService,
    router: Router,
    store: PassportDetailsStore,
    ui: UiStateService,
    syncService: SyncService,
    offlineStorage: OfflineStorageService,
    commentsNotification: CommentsNotificationService,
    helper: PassportHelperService,
    personaRouting: PersonaRoutingService,
    passportCache: PassportCacheService,
    private readonly historyPanelService: HistoryPanelService,
    private readonly commentsPanelService: CommentsPanelService
  ) {
    super(http, passports, searchService, filterService, router, store, ui, syncService, offlineStorage, commentsNotification, helper, personaRouting, passportCache);
  }

  override ngOnInit(): void {
    this.updateDesktopViewState();

    let restoring = peekExpectRestore();
    let snapshot = restoring ? parseReceiverListSnapshot() : null;
    if (restoring && !snapshot) {
      clearReceiverListRestoreState();
      restoring = false;
      snapshot = null;
    }
    this.pendingReceiverRestore = snapshot;

    if (!restoring) {
      this.filterService.clear();
      this.searchService.clear();
      this.ui.setSearchOpen(false);
      this.ui.setFiltersOpen(false);

      if (this.isDesktopView) {
        setTimeout(() => {
          this.ui.setStatusSelected('pending');
          this.generateFiltersFromRecords(this.passports.records());
        });
      }

      this.generateFiltersFromRecords(this.passports.records());
    }

    super.ngOnInit();
  }

  protected override shouldSkipDataReload(): boolean {
    if (peekExpectRestore()) {
      return false;
    }
    return super.shouldSkipDataReload();
  }

  protected override afterScrollRestored(): void {
    this.tryScrollRestoredPassportIntoView();
  }

  ngOnDestroy(): void {
    this.stopDesktopAutoScroll();
    if (this.desktopLongPressTimer) {
      clearTimeout(this.desktopLongPressTimer);
    }
    if (this.longPressTimer) {
      clearTimeout(this.longPressTimer);
    }
  }

  // Desktop Auto-scroll methods for drag operations
  private startDesktopAutoScroll(direction: 'up' | 'down', speed: number): void {
    // If already scrolling in the same direction with same speed, don't restart
    if (this.desktopAutoScrollAnimationId && this.desktopAutoScrollDirection === direction && this.desktopAutoScrollSpeed === speed) {
      return;
    }

    // Stop any existing scroll
    this.stopDesktopAutoScroll();

    this.desktopAutoScrollDirection = direction;
    this.desktopAutoScrollSpeed = speed;
    this.performDesktopAutoScroll();
  }

  private stopDesktopAutoScroll(): void {
    if (this.desktopAutoScrollAnimationId) {
      cancelAnimationFrame(this.desktopAutoScrollAnimationId);
      this.desktopAutoScrollAnimationId = null;
    }
    this.desktopAutoScrollDirection = null;
    this.desktopAutoScrollSpeed = 0;
  }

  private performDesktopAutoScroll(): void {
    if (!this.desktopAutoScrollDirection || this.desktopAutoScrollSpeed === 0) {
      return;
    }

    const scrollAmount = this.desktopAutoScrollDirection === 'up' ? -this.desktopAutoScrollSpeed : this.desktopAutoScrollSpeed;
    window.scrollBy({ top: scrollAmount, behavior: 'auto' });

    // Continue scrolling
    this.desktopAutoScrollAnimationId = requestAnimationFrame(() => this.performDesktopAutoScroll());
  }

  private handleDesktopAutoScrollDuringDrag(clientY: number): void {
    const viewportHeight = window.innerHeight;
    
    // Top scroll zone: The area from the top of the list content upwards.
    // The list starts after the header (DESKTOP_HEADER_HEIGHT).
    // We want to scroll up if the cursor is anywhere in the top zone (including over the header)
    // defined by DESKTOP_HEADER_HEIGHT + DESKTOP_AUTO_SCROLL_THRESHOLD.
    const topScrollBoundary = DESKTOP_HEADER_HEIGHT + DESKTOP_AUTO_SCROLL_THRESHOLD;
    
    // Check if near top edge (or over header)
    if (clientY < topScrollBoundary) {
      // Calculate intensity based on how far up we are
      // If clientY is at the boundary, ratio is 0.
      // If clientY is 0 (top of screen), ratio is (boundary - 0) / threshold = boundary / threshold > 1.
      // We clamp the ratio to 1 for max speed to avoid crazy scrolling.
      // Actually, let's normalize it:
      // We want max speed when clientY <= DESKTOP_HEADER_HEIGHT (i.e. over the header)
      // And linear decrease until topScrollBoundary.
      
      let ratio = 0;
      if (clientY <= DESKTOP_HEADER_HEIGHT) {
        ratio = 1; // Max speed if over header
      } else {
        // Range: [DESKTOP_HEADER_HEIGHT, topScrollBoundary] -> [1, 0]
        const distanceIntoZone = topScrollBoundary - clientY;
        ratio = distanceIntoZone / DESKTOP_AUTO_SCROLL_THRESHOLD;
      }
      
      const speed = Math.ceil(ratio * DESKTOP_MAX_SCROLL_SPEED);
      this.startDesktopAutoScroll('up', speed);
    }
    // Check if near bottom edge of viewport
    else if (clientY > viewportHeight - DESKTOP_AUTO_SCROLL_THRESHOLD) {
      const distanceFromEdge = viewportHeight - clientY;
      const ratio = (DESKTOP_AUTO_SCROLL_THRESHOLD - distanceFromEdge) / DESKTOP_AUTO_SCROLL_THRESHOLD;
      // Clamp ratio 0-1
      const clampedRatio = Math.min(Math.max(ratio, 0), 1);
      const speed = Math.ceil(clampedRatio * DESKTOP_MAX_SCROLL_SPEED);
      this.startDesktopAutoScroll('down', speed);
    }
    // Not near any edge, stop scrolling
    else {
      this.stopDesktopAutoScroll();
    }
  }

  get totalPages(): number {
    return this.helper.getTotalPages(this.visibleRecords.length, this.itemsPerPage);
  }

  protected override getVisibleRecordsForPagination(): PassportRecord[] {
    return this.visibleRecords;
  }

  get sortedPaginatedRecords(): PassportRecord[] {
    // 1. Sort (reads `visibleRecords` getter so tests can spy on it)
    let records = [...this.visibleRecords];
    if (this.sortColumn) {
      records.sort((a, b) => {
        const aDetails = this.store.get(a.id);
        const bDetails = this.store.get(b.id);
        // Handle scheduled column specially - convert to timestamp for proper date sorting
        let { valA, valB } = this.helper.getSortValues(this.sortColumn, a, b, aDetails, bDetails);
        if (this.sortColumn === 'scheduled') {
          valA = new Date(valA || 0).getTime();
          valB = new Date(valB || 0).getTime();
        }
        // Handle vendorCombined specially - receiver uses different format
        if (this.sortColumn === 'vendorCombined') {
          valA = `${aDetails?.vendorNumber || ''} - ${aDetails?.vendorName || ''}`;
          valB = `${bDetails?.vendorNumber || ''} - ${bDetails?.vendorName || ''}`;
        }
        return this.helper.compareValues(valA, valB, this.sortDirection);
      });
    }

    // 2. Paginate
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    return records.slice(startIndex, startIndex + this.itemsPerPage);
  }

  onStatusTabSelect(status: 'pending' | 'outgoing' | 'completed'): void {
    this.ui.setStatusSelected(status);
    this.currentPage = 1; // Reset page when switching tabs
    // Regenerate filters based on the new tab's records
    this.generateFiltersFromRecords(this.passports.records());
  }

  openPassportDetails(id: string): void {
    // Prevent navigation if user just finished a long press or drag
    // OR if a drag is currently active (desktopDragActiveRecordId is set)
    if (this.ignoreNextClick || (this.desktopDragActiveRecordId && this.desktopDragActiveRecordId === id)) {
      console.log('[Receiver] Navigation prevented due to active drag/long-press');
      this.ignoreNextClick = false;
      return;
    }

    const filtersRaw = this.filterService.filters();
    const filtersClone: Record<string, string[]> = {};
    for (const key of Object.keys(filtersRaw)) {
      const vals = filtersRaw[key];
      filtersClone[key] = vals ? [...vals] : [];
    }

    const tab = this.ui.statusSelected();
    const scrollY =
      globalThis.window === undefined
        ? undefined
        : globalThis.window.scrollY || globalThis.document?.documentElement?.scrollTop || 0;

    // Desktop "Show rows" (pagination size); mobile uses same field for consistency when restoring
    const showRowsPerPage = normalizeReceiverDesktopShowRows(this.itemsPerPage);

    saveReceiverListSnapshot({
      tab,
      filters: filtersClone,
      searchQuery: this.searchService.query(),
      currentPage: this.currentPage,
      itemsPerPage: showRowsPerPage,
      sortColumn: this.sortColumn,
      sortDirection: this.sortDirection,
      passportId: id,
      scrollY: scrollY === 0 ? undefined : scrollY
    });

    this.ui.setExpandedRecord(id);
    this.scrollPositionService.saveScrollPosition('/receiver');
    void this.router.navigate(['/passport', id], {
      state: { from: 'receiver', tab: this.ui.statusSelected() }
    });
  }

  /**
   * Build header object for comments/history panels
   */
  protected override buildPanelHeader(id: string): { loadNumber: string; variety: string; haulier: string; scheduledText: string; origin: string; destination: string; apnName: string; vendorName: string; vendorNumber: string; bulkerNumber: string } | undefined {
    const details = this.store.get(id);
    return this.helper.buildPanelHeader(details, this.dateTimeHelper);
  }

  openHistory(id: string): void {
    const header = this.buildPanelHeader(id);
    if (header) {
      this.historyPanelService.openHistoryPanel(id, header);
    }
  }

  openComments(id: string): void {
    const header = this.buildPanelHeader(id);
    if (header) {
      this.commentsPanelService.openCommentsPanel(id, header);
    }
  }

  // Desktop Drag and Drop
  
  onDesktopMouseEnter(recordId: string): void {
    if (this.isShuntingTab && !this.sortColumn) {
      this.desktopHoveredRecordId = recordId;
    }
  }

  onDesktopMouseLeave(): void {
    // Only clear hover if not actively dragging
    if (!this.draggedRecordId) {
      this.desktopHoveredRecordId = null;
    }
  }

  onDesktopMouseDown(recordId: string, event: MouseEvent): void {
    if (!this.isShuntingTab) return;
    // Only left click
    if (event.button !== 0) return;
    
    this.ignoreNextClick = false;
    this.desktopLongPressTimer = setTimeout(() => {
      this.desktopDragActiveRecordId = recordId;
      this.ignoreNextClick = true; // Long press activated, ignore next click
    }, 500);
  }

  onDesktopMouseUp(): void {
    if (this.desktopLongPressTimer) {
      clearTimeout(this.desktopLongPressTimer);
      this.desktopLongPressTimer = null;
    }
    // If we are not dragging, clear the active record
    if (!this.draggedRecordId) {
        this.desktopDragActiveRecordId = null;
    }
  }

  // New: Clear active state on drag end
  onDesktopDragEnd(): void {
    // Stop any active auto-scroll
    this.stopDesktopAutoScroll();
    
    this.draggedRecordId = null;
    this.desktopDragActiveRecordId = null;
    // Clear hover state after drag ends
    this.desktopHoveredRecordId = null;
    // Ensure ignoreNextClick is reset to false because drag overrides click naturally,
    // but we want to ensure subsequent clicks work.
    // However, if drag happened, 'click' event is suppressed by browser. 
    // If long press happened but no drag, 'click' fires and we consume it in openPassportDetails.
    // So resetting here handles the drag case.
    setTimeout(() => { this.ignoreNextClick = false; }, 100);
  }

  onDesktopDragStart(event: DragEvent, recordId: string, index: number): void {
    if (!this.isShuntingTab) return;
    this.draggedRecordId = recordId;
    event.dataTransfer?.setData('text/plain', JSON.stringify({ id: recordId, index }));
    event.dataTransfer!.effectAllowed = 'move';
    // Add a transparent image or default to avoid the big row image if desired, but default is fine
  }

  onDesktopDragOver(event: DragEvent): void {
    if (!this.isShuntingTab) return;
    event.preventDefault(); // Essential to allow dropping
    event.dataTransfer!.dropEffect = 'move';
    
    // Handle auto-scroll when near screen edges
    this.handleDesktopAutoScrollDuringDrag(event.clientY);
  }

  async onDesktopDrop(event: DragEvent, toIndex: number): Promise<void> {
    // Stop any active auto-scroll
    this.stopDesktopAutoScroll();
    
    if (!this.isShuntingTab) return;
    event.preventDefault();
    const data = event.dataTransfer?.getData('text/plain');
    if (!data) return;
    
    const { index: fromIndex } = JSON.parse(data);
    
    const realFromIndex = (this.currentPage - 1) * this.itemsPerPage + fromIndex;
    const realToIndex = (this.currentPage - 1) * this.itemsPerPage + toIndex;

    await this.onReorder(realFromIndex, realToIndex);
    this.draggedRecordId = null;
    this.desktopDragActiveRecordId = null; // Reset drag active state
  }

  /**
   * Override to disable offline caching for desktop view
   */
  protected override async afterPassportsLoaded(headers: any, passports: PassportMainWithDraftDto[]): Promise<void> {
    await this.loadReceivedPassports(headers);

    if (this.pendingReceiverRestore) {
      this.applyReceiverListRestore(this.pendingReceiverRestore);
      this.pendingReceiverRestore = null;
    }
  }

  private applyReceiverListRestore(snapshot: ReceiverListSnapshot): void {
    this.filterService.clear();
    this.searchService.clear();
    this.ui.setStatusSelected(snapshot.tab);
    for (const key of Object.keys(snapshot.filters)) {
      const values = snapshot.filters[key];
      if (values?.length) {
        this.filterService.setFilter(key, values);
      }
    }
    this.searchService.setQuery(snapshot.searchQuery);
    this.searchService.syncFilterQueryWithQuery();
    this.rowsDropdownOpen = false;
    this.itemsPerPage = normalizeReceiverDesktopShowRows(snapshot.itemsPerPage);
    this.sortColumn = snapshot.sortColumn;
    this.sortDirection = snapshot.sortDirection;
    this.currentPage = snapshot.currentPage;
    const totalPages = this.helper.getTotalPages(this.visibleRecords.length, this.itemsPerPage);
    if (this.currentPage > totalPages) {
      this.currentPage = totalPages;
    }
    if (this.currentPage < 1) {
      this.currentPage = 1;
    }
    this.generateFiltersFromRecords(this.passports.records());
    this.passportIdToScrollAfterRestore = snapshot.passportId;
    clearReceiverListRestoreState();
  }

  private tryScrollRestoredPassportIntoView(): void {
    const id = this.passportIdToScrollAfterRestore;
    if (!id) {
      return;
    }
    this.passportIdToScrollAfterRestore = null;
    if (globalThis.document === undefined) {
      return;
    }
    requestAnimationFrame(() => {
      const safeId = id.replaceAll('"', '');
      const el = globalThis.document.querySelector(`[data-passport-id="${safeId}"]`);
      el?.scrollIntoView({ block: 'nearest', behavior: 'instant' });
    });
  }

  protected override async loadFromOfflineStorage(): Promise<void> {
    // Offline mode not supported for Receiver persona
    console.log('Offline mode not supported for Receiver');
  }

  /**
   * Override status mapping for receiver-specific logic
   */
  protected override mapStatusToTab(statusName: string): 'pending' | 'outgoing' | 'completed' {
    // Map status based on statusCode:
    // "RECEIVED_BY_RECEIVER" -> 'outgoing' (Receiving tab)
    // All other statuses -> 'pending' (Shunting tab)
    const statusCode = statusName.toUpperCase();
    return statusCode === 'RECEIVED_BY_RECEIVER' ? 'outgoing' : 'pending';
  }

  /**
   * Override to store filters in receiverFilters property
   * Uses all records (not filtered) to ensure filter options remain available
   */
  protected override generateFiltersFromRecords(records: PassportRecord[]): void {
    const relevantRecords = this.passports.records();

    const originSet = new Set<string>();
    const destinationSet = new Set<string>();
    const apnSet = new Set<string>();
    let hasEmptyOrigin = false;

    for (const rec of relevantRecords) {
      const details = this.store.get(rec.id);
      
      // Check if this record has an empty origin
      if (details?.origin?.trim()) {
        originSet.add(details.origin);
      } else {
        hasEmptyOrigin = true;
      }
      
      const destinationValue = details?.destinationWeighbridge || details?.destination;
      if (destinationValue) destinationSet.add(destinationValue);
      // Prefer APN Name, fallback to APN code
      const apnVal = details?.apnName || details?.apn;
      if (apnVal) apnSet.add(apnVal);
    }

    // Sort origin options, keeping "No Origin Field" first if applicable
    const sortedOrigins = Array.from(originSet).sort((a, b) => a.localeCompare(b));
    const originOptions = hasEmptyOrigin ? [this.helper.NO_ORIGIN_FIELD_TEXT, ...sortedOrigins] : sortedOrigins;

    this.receiverFilters = [
      { id: 'apnName', label: 'APN/Name', options: Array.from(apnSet).sort((a, b) => a.localeCompare(b)) },
      { id: 'destination', label: 'Destination', options: Array.from(destinationSet).sort((a, b) => a.localeCompare(b)) },
      { id: 'origin', label: 'Origin', options: originOptions }
    ];
  }

  /**
   * Override to add receiver-specific fields
   */
  protected override loadPassportMainDetails(passports: PassportMainWithDraftDto[]): void {
    for (const p of passports) {
      const id = p.passportID?.toString() || '';
      if (!id) continue;

      this.store.update(id, {
        id: id,
        loadNumber: p.loadNumber || '',
        variety: p.varietyName || p.variety || '',
        haulier: p.haulierName || '',
        bulkerNumber: p.trailerNumber || '',
        bulkerMain: p.trailerMain || '',  // Store bulkerMain for display
        vendorNumber: p.vendorNumber || '',
        vendorName: p.vendorName || '',
        origin: this.helper.formatOriginWithType(p.originName, p.rawSourceType),
        destination: p.destinationName || p.destination || '',
        destinationWeighbridge: p.destinationWeighbridge || '',
        apn: p.apn || '',
        apnName: p.apnName || '',
        plantID: p.plantID,  // Store plant ID
        isShuntingRequired: p.isShuntingRequired || false,  // Store isShuntingRequired
        dateTime: this.dateTimeHelper.ensureUtcTimestamp(p.dueDateTime || p.scheduledDateTime || ''),  // Store scheduled/due date time
        dueDateTime: this.dateTimeHelper.ensureUtcTimestamp(p.dueDateTime || p.scheduledDateTime || ''),  // Prefer dueDateTime from API
        loadingDateTime: this.dateTimeHelper.ensureUtcTimestamp(p.loadingDateTime || ''),
        imageCount: p.imageCount || 0,  // Store image count from backend
        haccpCheck: p.haccpCheck ? 'Yes' : 'No',  // Store HACCP check value
        haccpImageLinks: p.haccpImageLinks || [],  // Store image links
        haccpImages: [],  // Will be populated if images are loaded
        deletedImageFilenames: [],  // Clear old editing state when refreshing from API
        haccpImageFiles: [],  // Clear old editing state when refreshing from API
        hasSeenPcrChanges: p.hasSeenPcrChanges || false,  // Store PCR changes flag
        isPassportCompleted: p.isPassportCompleted || false,  // Store completion status
        isReceivingCompleted: p.isReceivingCompleted === true,
        statusCode: p.statusCode || '',  // Store status code
        isCancelled: (p.loadStatusName || '').toLowerCase() === 'cancelled',  // Case-insensitive check
        loadStatusName: p.loadStatusName || '',  // Store load status name
        authorisedBy: p.authorizerName || '',
        authorisedByUserID: p.authorisedByUserID || undefined
      });
    }
  }


  private async loadReceivedPassports(headers: any): Promise<void> {
    console.log('🔄 [loadReceivedPassports] Starting...');
    try {
      // Backend will get PlantID from ReceiverPlant mapping using UserID from JWT token
      const receivedPassportsUrl = `${environment.apiBaseUrl}/api/v1/passportorder/getreceivedpassports`;
      console.log(`🔍 [loadReceivedPassports] Fetching received passports from: ${receivedPassportsUrl}`);
      
      const receivedPassports = await firstValueFrom(
        this.http.get<PassportMainWithDraftDto[]>(receivedPassportsUrl, { headers })
      );

      console.log(`📦 [loadReceivedPassports] Received passports API response:`, receivedPassports);
      console.log(`📊 [loadReceivedPassports] Received passports count: ${receivedPassports?.length || 0}`);

      if (!receivedPassports || receivedPassports.length === 0) {
        console.log('⚠️ [loadReceivedPassports] No received passports found - API returned empty or null');
        return;
      }

      // Convert received passports to records with 'outgoing' status (Received tab)
      const receivedRecords: PassportRecord[] = receivedPassports.map((p): PassportRecord => {
        const receivingTabStatus: 'outgoing' | 'completed' =
          p.isReceivingCompleted === true ? 'completed' : 'outgoing';
        return {
          id: String(p.passportID),
          loadNumber: p.loadNumber || '',
          variety: p.variety || '',
          haulier: p.haulierName || '',
          bulker: p.trailerMain || p.trailerNumber || '',  // ✅ FIXED - Added bulker field
          dueDateTime: this.dateTimeHelper.ensureUtcTimestamp(p.dueDateTime || ''),  // ✅ FIXED - Removed invalid scheduledDateTime fallback
          status: receivingTabStatus,
          isSynced: true, // Fetched from API
          origin: this.helper.formatOriginWithType(p.originName, p.rawSourceType),  // ✅ CORRECT
          destination: p.destination || '',  // ✅ FIXED - Backend sends destination, not destinationName
          destinationWeighbridge: p.destinationWeighbridge || '',
          apn: p.apn || '',
          vendorName: p.vendorName || '',
          statusCode: p.statusCode || '',
          statusName: p.statusDisplayName || p.statusName || ''  // ✅ Uses statusDisplayName from backend
        };
      });

      // Add received records to the existing records
      const currentRecords = this.passports.records();
      this.passports.records.set([...currentRecords, ...receivedRecords]);

      // Load details for received records
      this.loadPassportMainDetails(receivedPassports);

      // Regenerate filters to include received passports
      this.generateFiltersFromRecords(this.passports.records());

      console.log(`[Receiver] Added ${receivedRecords.length} received passports to records`);

    } catch (error) {
      console.warn('Error loading received passports:', error);
      // Don't fail the whole load if received passports fail
    }
  }



  /**
   * Build searchable text from record and details
   */
  private buildSearchableText(rec: PassportRecord, details: any): string {
    // Use "No Origin Field" as searchable text when origin is empty
    const recOrigin = rec.origin?.trim() ? rec.origin : this.helper.NO_ORIGIN_FIELD_TEXT;
    const detailsOrigin = details?.origin?.trim() ? details.origin : this.helper.NO_ORIGIN_FIELD_TEXT;
    
    return [
      rec.loadNumber || '',
      details?.loadNumber || '',
      rec.variety || '',
      details?.variety || '',
      rec.haulier || '',
      details?.haulier || '',
      rec.bulker || '',
      details?.bulkerNumber || '',
      rec.growerName || '',
      rec.vendorName || '',
      details?.vendorName || '',
      rec.vendorNumber || '',
      details?.vendorNumber || '',
      recOrigin,
      detailsOrigin,
      rec.destinationWeighbridge || rec.destination || '',
      details?.destinationWeighbridge || details?.destination || '',
      rec.apnName || '',
      details?.apnName || '',
      rec.apn || '',
      details?.apn || ''
    ].join(' ').toLowerCase();
  }

  /**
   * Whether a row matches the debounced search string and active column filters.
   */
  private recordMatchesSearchAndFilters(
    r: PassportRecord,
    queryNormalized: string,
    filters: Record<string, string[]>
  ): boolean {
    const details = this.store.get(r.id);

    if (queryNormalized.length > 0) {
      const searchable = this.buildSearchableText(r, details);
      if (!searchable.includes(queryNormalized)) {
        return false;
      }
    }

    if (filters['origin']?.length) {
      if (!this.helper.matchesFilter(filters, 'origin', details?.origin)) {
        return false;
      }
    }
    if (filters['destination']?.length) {
      const destinationValue = details?.destinationWeighbridge || details?.destination;
      if (!this.helper.matchesFilter(filters, 'destination', destinationValue)) {
        return false;
      }
    }
    if (filters['haulier']?.length) {
      if (!this.helper.matchesFilter(filters, 'haulier', r.haulier)) {
        return false;
      }
    }
    if (filters['apnName']?.length) {
      const val = details?.apnName || details?.apn;
      if (!val || !filters['apnName'].includes(val)) {
        return false;
      }
    }

    return true;
  }

  /**
   * Apply debounced search (`filterQuery`) and filters to a record list (e.g. unit tests).
   */
  private applySearchAndFilters(records: PassportRecord[]): PassportRecord[] {
    const query = (this.searchService.filterQuery() || '').trim().toLowerCase();
    const filters = this.filterService.filters();
    return records.filter((r) => this.recordMatchesSearchAndFilters(r, query, filters));
  }

  /**
   * Helper method to get count by status
   */
  private getCountByStatus(status: string): number {
    const d = this.receiverListDerived();
    if (status === 'pending') {
      return d.shuntingCount;
    }
    if (status === 'outgoing') {
      return d.receivingCount;
    }
    if (status === 'completed') {
      return d.completedCount;
    }
    return 0;
  }

  get statusFilter() {
    return this.ui.statusSelected;
  }

  get visibleRecords(): PassportRecord[] {
    return this.receiverListDerived().visibleRecords;
  }

  get shuntingCount(): number {
    return this.receiverListDerived().shuntingCount;
  }

  get receivingCount(): number {
    return this.receiverListDerived().receivingCount;
  }

  get completedCount(): number {
    return this.receiverListDerived().completedCount;
  }

  // Drag and drop handlers
  onLongPressStart(recordId: string, event: TouchEvent | MouseEvent): void {
    this.longPressTimer = setTimeout(() => {
      this.longPressActive = true;
      this.draggedRecordId = recordId;
    }, 500); // 500ms long press
  }

  onLongPressEnd(): void {
    if (this.longPressTimer) {
      clearTimeout(this.longPressTimer);
      this.longPressTimer = null;
    }
  }

  onDragStart(recordId: string): void {
    this.draggedRecordId = recordId;
    this.longPressActive = true;
  }

  onDragEnd(): void {
    this.draggedRecordId = null;
    this.longPressActive = false;
  }

  async onReorder(fromIndex: number, toIndex: number): Promise<void> {
    // Only allow reordering on Shunting tab
    if (!this.isShuntingTab) {
      console.warn('⚠️ Drag-and-drop is disabled on Receiving tab');
      return;
    }
    
    if (fromIndex === toIndex) return;

    // records = copy of visible records
    const records = [...this.visibleRecords];
    const [movedItem] = records.splice(fromIndex, 1);
    records.splice(toIndex, 0, movedItem);

    // Reconstruct allRecords
    // We need to preserve the records NOT in `visibleRecords`.
    const allRecords = this.passports.records();
    const visibleIds = new Set(records.map(r => r.id));
    const otherRecords = allRecords.filter(r => !visibleIds.has(r.id));
    
    // Recombine: Put new ordered visible records first, then others (or vice versa, doesn't matter for filtered views)
    const newAllRecords = [...records, ...otherRecords]; 
    
    this.passports.records.set(newAllRecords);

    // Calculate steps for API call
    // Positive = move up (smaller index), Negative = move down (larger index)
    const steps = fromIndex - toIndex;
    
    // Check if this is a mock/dummy record (starts with MOCK-)
    if (movedItem.id.toString().startsWith('MOCK-')) {
      console.log(`✅ [Mock] Reordered dummy passport ${movedItem.id} locally (skipping API call)`);
      return;
    }

    // Call reorder API
    await this.reorderPassport(Number.parseInt(movedItem.id), steps);
  }

  private async reorderPassport(passportId: number, steps: number): Promise<void> {
    try {
      const token = localStorage.getItem('accessToken');
      const tokenType = localStorage.getItem('tokenType') || 'Bearer';
      
      console.log(`🔄 Reordering passport ${passportId} by ${steps} steps`);

      const headers = {
        'Authorization': `${tokenType} ${token}`,
        'Content-Type': 'application/json'
      };

      // Use new API route without plantId (backend gets it from passport)
      const url = `${environment.apiBaseUrl}/api/v1/passportorder/move/${passportId}/by/${steps}`;
      
      await firstValueFrom(
        this.http.post(url, {}, { headers, withCredentials: true })
      );

      console.log(`✅ Reordered passport ${passportId} successfully`);
      
      // Reload passports to sync with backend order
      await this.loadPassportsFromAPI();
    } catch (error) {
      console.warn('Error reordering passport:', error);
      // Reload to sync with server state
      await this.loadPassportsFromAPI();
    }
  }
}
